import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useCallback, useState } from 'react';

import { Grid } from '@/components/core/Grid';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { PanelBody } from '@/components/core/Panel/PanelBody';
import { useDynamicPageSize } from '@/hooks/useDynamicPageSize';
import { useTranslation } from '@/hooks/useTranslation';

import { columns } from './columns';
import { Toolbar } from './components/Toolbar';
import { useModules, useMonitoringData } from './monitor.queries';

const timestamps = [
    'ALL',
    'ONE_DAY',
    'TWO_DAYS',
    'THREE_DAYS',
    'ONE_WEEK',
    'ONE_MONTH',
];

export default function MonitorPage() {
    const { t } = useTranslation();
    const [ selectedModule, setSelectedModule ] = useState<string | undefined>(undefined);
    const [ textFilter, setTextFilter ] = useState<string | undefined>(undefined);
    const [ selectedTimestamp, setSelectedTimestamp ] = useState<string | undefined>(undefined);

    const pageSize = useDynamicPageSize();
    const { data: monitoringData, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useMonitoringData({
        variables: {
            limit: pageSize,
            module: selectedModule,
            term: textFilter,
            datefilter: selectedTimestamp
        },

        select: data => data.pages.flatMap(page => page.data),

        enabled: pageSize > 0
    });
    const { data: modules } = useModules();
    const monitoringGridColumns = columns(t);

    const onResetFiltersClick = () => {
        setTextFilter('');
        setSelectedModule('');
        setSelectedTimestamp('');
    };

    return (
        <Panel>
            <PanelHeader
                icon={ClipboardDocumentListIcon}
                title={t('MONITORING')}
                subTitle={t('MONITORING.SUB_TITLE')}
            />
            <PanelBody className="tw-h-[70vh]">
                <Grid
                    toolbar={() => (
                        <Toolbar
                            modules={modules?? []}
                            textFilter={textFilter}
                            onTextFilterChange={setTextFilter}
                            selectedModule={selectedModule}
                            onSelectedModuleChange={setSelectedModule}
                            onResetFiltersClick={onResetFiltersClick}
                            timestamps={timestamps}
                            selectedTimestamp={selectedTimestamp}
                            onSelectedTimestampChange={setSelectedTimestamp}
                        />
                    )}
                    columns={monitoringGridColumns}
                    isLoading={isLoading}
                    isLoadingNextPage={isFetchingNextPage}
                    data={monitoringData ?? []}
                    onInfiniteScroll={useCallback(() => hasNextPage && fetchNextPage(), [hasNextPage, fetchNextPage])}
                    className="tw-h-full"
                />
            </PanelBody>
        </Panel>
    );
}
